import React, { useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { useNotifications } from "@presentation/components/notifications";
import type { ProjectDetail } from "@domain/schemas/workspace";
import { useWorkspaceSuccessNotification } from "@presentation/components/notifications/useWorkspaceSuccessNotification";

interface RenameProjectDialogProps {
  currentName: string;
  onRename: (newName: string) => Promise<ProjectDetail>;
  onClose: () => void;
  onSuccess: (updated: ProjectDetail) => void;
}

/**
Renaming a project - the caller supplies `onRename` since the action is reachable both from a
project's own Configuration page and from its row on the team's project inventory, each injecting
a different (but equivalent) application service.
*/
export const RenameProjectDialog: React.FC<RenameProjectDialogProps> = ({ currentName, onRename, onClose, onSuccess }) => {
  const [name, setName] = useState(currentName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { pushNotification } = useNotifications();
  const notifySuccess = useWorkspaceSuccessNotification();

  const canSubmit = name.trim().length > 0 && name.trim() !== currentName && !isSubmitting;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const updated = await onRename(name.trim());
      notifySuccess("project", "renamed");
      onSuccess(updated);
      onClose();
    } catch (error) {
      pushNotification(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not rename the project.", "error", 10_000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog title="Rename project" onClose={onClose} width={420}>
      <div style={{ display: "grid", gap: 16 }}>
        <div>
          <label htmlFor="rename-project-name" style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
            Project name
          </label>
          <Input id="rename-project-name" style={{ width: "100%" }} value={name} onChange={(event) => setName(String(event.value ?? ""))} />
        </div>
      </div>
      <DialogActionsBar>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button themeColor="primary" disabled={!canSubmit} onClick={handleSubmit}>
          Save
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};
